import { useState, useContext, useMemo, useCallback, useRef, useEffect, Fragment } from 'react';
import {
    Flex, Box, Text, Skeleton, VStack,
    Table, Thead, Tbody, Tr, Td, Icon, HStack,
    Switch, IconButton, Tooltip, Divider,
    Input, InputGroup, InputLeftElement,
    Popover, PopoverTrigger, PopoverContent, PopoverBody, Portal,
    Menu, MenuButton, MenuList, MenuItem, Button,
} from '@chakra-ui/react';
import { TbArrowUp, TbArrowDown, TbDownload, TbMaximize, TbMinimize, TbSearch, TbUsers, TbUserCog, TbChevronDown, TbEye, TbMapPin, TbExternalLink, TbCheck } from 'react-icons/tb';
import { useProductivityStats } from '../../../hooks/reporting/useProductivityStats';
import { useReportingFilters } from '../ReportingFiltersContext';
import ExcludeEvsToggle from '../components/ExcludeEvsToggle';
import SessionContext from '../../../contexts/SessionContext';
import { ProductivityStatItem, ProductivityLocationBreakdown, ProductivityInactiveUser, ReportingComparison } from '../../../types/reporting';
import { getReportingComparison, isReportingComparisonSupported } from '../shared/reportingComparisonUtils';
import SortArrowIcon, { SortIconDirection } from '../../../components/SortArrowIcon';
import ResizableTh from '../../../components/ResizableTh';
import ReportingFullScreenShell from '../components/ReportingFullScreenShell';
import ReportingTableSettingsDrawer from '../components/ReportingTableSettingsDrawer';
import { useTableSettings } from '../../../hooks/useTableSettings';
import { useColumnWidths } from '../../../hooks/useColumnWidths';
import { useReportingColumnPrefs } from '../../../hooks/useReportingColumnPrefs';
import { loadViewPrefs, saveViewPrefs } from '../../../hooks/reporting/useReportingViewPrefs';
import { exportToCsv } from '../../../utils/csvExport';
import {
    nestedRowsToPrefs,
    prefsToNestedRows,
    type NestedColumnGroupDef,
} from '../nestedColumnGroups';

interface ProductivityTabProps {
    startDate: string;
    endDate: string;
    locationId?: string;
    granularity: 'day' | 'week' | 'biweekly' | 'month';
    preset?: string;
    isActive?: boolean;
}

type SortField = keyof ProductivityStatItem | 'name' | 'pctMissedOpportunity' | 'dailyActiveToggle' | 'weeklyActiveToggle';

type DailyMetric = 'average' | 'median';
type WeeklyMetric = 'total' | 'average' | 'median';

const DAILY_FIELD: Record<DailyMetric, keyof ProductivityStatItem> = {
    average: 'dailyAvgMinutes',
    median: 'dailyMedianMinutes',
};
const WEEKLY_FIELD: Record<WeeklyMetric, keyof ProductivityStatItem> = {
    total: 'weeklyTotalMinutes',
    average: 'weeklyAvgMinutes',
    median: 'weeklyMedianMinutes',
};
const DAILY_OPTIONS: { value: DailyMetric; short: string; label: string; }[] = [
    { value: 'average', short: 'Avg', label: 'Average' },
    { value: 'median', short: 'Median', label: 'Median' },
];
const WEEKLY_OPTIONS: { value: WeeklyMetric; short: string; label: string; }[] = [
    { value: 'total', short: 'Total', label: 'Total' },
    { value: 'average', short: 'Avg', label: 'Average' },
    { value: 'median', short: 'Median', label: 'Median' },
];

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
const formatPercent = (value: number) => `${(value || 0).toFixed(1)}%`;
const formatNumber = (value: number) => (value || 0).toLocaleString();
const formatDecimal = (value: number) => (value || 0).toFixed(2);
const formatDuration = (minutes: number) => {
    if (!minutes) return '—';
    if (minutes >= 60) return `${(minutes / 60).toFixed(1)} hr`;
    return `${Math.round(minutes)} min`;
};

interface ColumnDef {
    key: SortField;
    label: string;
    format: (v: number) => string;
    restrictedToRoles?: string[];
    adminOnly?: boolean;
    isNegative?: boolean;
    breakdownKey?: keyof ProductivityLocationBreakdown;
    /** Screen-time columns whose value/label are driven by an aggregation dropdown. */
    aggregation?: 'daily' | 'weekly';
}

const ALL_COLUMNS: ColumnDef[] = [
    { key: 'quoteCount', label: 'Quote Count', format: formatNumber, breakdownKey: 'quoteCount' },
    { key: 'quotesPerOrder', label: 'Quotes / Order', format: formatDecimal },
    { key: 'orderCount', label: 'Order Count', format: formatNumber, restrictedToRoles: ['executive', 'central-parts', 'parts-manager'], breakdownKey: 'orderCount' },
    { key: 'orderVolume', label: 'Order Volume', format: formatCurrency, restrictedToRoles: ['executive', 'central-parts', 'parts-manager'], breakdownKey: 'orderVolume' },
    { key: 'oemEndorsementCount', label: 'OEM Endorsement', format: formatNumber },
    { key: 'expeditedCount', label: 'Expedited', format: formatNumber },
    { key: 'dealerReferralCount', label: 'Dealer Referral', format: formatNumber },
    { key: 'quotesOnlyCount', label: 'Quotes Only', format: formatNumber },
    { key: 'potentialTotalCount', label: 'Potential Totals', format: formatNumber },
    { key: 'incorrectPartNumbers', label: 'Incorrect Part #s', format: formatNumber, breakdownKey: 'incorrectPartNumbers' },
    { key: 'partsWithoutPartNumber', label: 'No Part #', format: formatNumber },
    { key: 'manuallyFlaggedParts', label: 'Manually Flagged', format: formatNumber, breakdownKey: 'manuallyFlaggedParts' },
    { key: 'totalFlaggedParts', label: 'Total Flagged', format: formatNumber, breakdownKey: 'totalFlaggedParts' },
    { key: 'pctPartsFlagged', label: '% Flagged', format: formatPercent, breakdownKey: 'pctPartsFlagged' },
    { key: 'totalMissedOpportunity', label: 'Missed Opportunity', format: formatCurrency, isNegative: true, breakdownKey: 'totalMissedOpportunity' },
    { key: 'pctMissedOpportunity', label: '% Missed Opportunity', format: formatPercent, isNegative: true },
    { key: 'avgMissedOpportunity', label: 'Avg. Missed Opportunity', format: formatCurrency, isNegative: true },
    { key: 'medianMissedOpportunity', label: 'Median Missed Opportunity', format: formatCurrency, isNegative: true },
    { key: 'dailyActiveToggle', label: 'Daily Active Session', format: formatDuration, adminOnly: true, aggregation: 'daily' },
    { key: 'weeklyActiveToggle', label: 'Weekly Active Session', format: formatDuration, adminOnly: true, aggregation: 'weekly' },
];

/** Columns picker nesting — Total / Avg / Median only. Active Session stays flat. */
const PRODUCTIVITY_COLUMN_GROUPS: NestedColumnGroupDef[] = [
    {
        id: 'missedOpportunity',
        label: 'Missed Opportunity',
        children: [
            { key: 'totalMissedOpportunity', label: 'Total' },
            { key: 'avgMissedOpportunity', label: 'Avg.' },
            { key: 'medianMissedOpportunity', label: 'Median' },
        ],
        defaultChildKeys: ['totalMissedOpportunity', 'avgMissedOpportunity'],
    },
];

const CSV_HEADERS: Record<string, string> = {
    firstName: 'First Name',
    lastName: 'Last Name',
    position: 'Position',
    quoteCount: 'Quote Count',
    quotesPerOrder: 'Quotes / Order',
    orderCount: 'Order Count',
    orderVolume: 'Order Volume',
    oemEndorsementCount: 'OEM Endorsement',
    expeditedCount: 'Expedited',
    dealerReferralCount: 'Dealer Referral',
    quotesOnlyCount: 'Quotes Only',
    potentialTotalCount: 'Potential Totals',
    incorrectPartNumbers: 'Incorrect Part #s',
    partsWithoutPartNumber: 'No Part #',
    manuallyFlaggedParts: 'Manually Flagged',
    totalFlaggedParts: 'Total Flagged',
    manualOverrides: 'Manual Overrides',
    pctPartsFlagged: '% Flagged',
    totalMissedOpportunity: 'Missed Opportunity',
    pctMissedOpportunity: '% Missed Opportunity',
    avgMissedOpportunity: 'Avg. Missed Opportunity',
    medianMissedOpportunity: 'Median Missed Opportunity',
    dailyActiveToggle: 'Daily Active Session (min)',
    weeklyActiveToggle: 'Weekly Active Session (min)',
};

type UserFilter = 'everyone' | 'central-parts';

const positionMap: Record<string, string> = {
    'central-parts': 'Central Parts',
    'parts-manager': 'Parts Manager',
    estimator: 'Estimator',
    executive: 'Executive',
    gm: 'GM',
    other: 'Other',
    technician: 'Technician',
};

const DEFAULT_SORT_BY: SortField = 'orderVolume';
const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'desc';

const VIEW_MODE_CONFIG: { key: UserFilter; label: string; icon: React.ElementType; }[] = [
    { key: 'everyone', label: 'Everyone', icon: TbUsers as React.ElementType },
    { key: 'central-parts', label: 'Central Parts', icon: TbUserCog as React.ElementType },
];

interface OrderRef {
    orderId: string;
    roNumber: string;
}

const OrderListPopover = ({
    orders,
    label,
    children,
}: {
    orders: OrderRef[];
    label: string;
    children: React.ReactNode;
}) => {
    return (
        <Popover trigger="hover" isLazy openDelay={250} closeDelay={150} placement="top-end">
            <PopoverTrigger>
                <Box
                    display="inline-block"
                    cursor="pointer"
                    borderBottom="1px dashed"
                    borderColor="gray.300"
                    onClick={(e) => e.stopPropagation()}
                >
                    {children}
                </Box>
            </PopoverTrigger>
            <Portal>
                <PopoverContent
                    w="240px"
                    maxH="300px"
                    borderRadius="md"
                    boxShadow="none"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Box px={3} py={2} borderBottomWidth="1px" borderColor="gray.100">
                        <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                            {label} ({orders.length})
                        </Text>
                    </Box>
                    <PopoverBody p={0} overflowY="auto" maxH="256px">
                        <VStack spacing={0} align="stretch" py={1}>
                            {orders.map((o) => (
                                <HStack
                                    key={o.orderId}
                                    as="a"
                                    href={`/order/${o.orderId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    px={3}
                                    py={1.5}
                                    spacing={1.5}
                                    cursor="pointer"
                                    _hover={{ bg: 'gray.50', textDecoration: 'none' }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Text fontSize="xs" color="#0071E3" fontWeight="medium" flex={1} noOfLines={1}>
                                        RO #{o.roNumber || o.orderId.slice(-6)}
                                    </Text>
                                    <Icon as={TbExternalLink as React.ElementType} boxSize={3} color="gray.400" />
                                </HStack>
                            ))}
                        </VStack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};

const ChangeIndicator = ({ value, isNegative }: { value?: number; isNegative?: boolean; }) => {
    if (value === undefined || value === null) {
        return (
            <Text as="span" fontSize="xs" color="gray.400" ml={2} fontStyle="italic">
                —
            </Text>
        );
    }
    if (value === 0) return null;
    const isPositiveChange = isNegative ? value < 0 : value > 0;
    const color = isPositiveChange ? 'green.500' : 'red.500';
    const IconComponent = value > 0 ? TbArrowUp : TbArrowDown;
    return (
        <Flex align="center" gap={0.5} fontStyle="italic" ml={2}>
            <Icon as={IconComponent as React.ElementType} boxSize={3} color={color} />
            <Text fontSize="xs" color={color} fontWeight="medium">
                {Math.abs(value).toFixed(1)}%
            </Text>
        </Flex>
    );
};

function AggregationHeaderMenu<T extends string>({
    prefix,
    options,
    value,
    onChange,
    sortDirection,
    onSort,
    active,
}: {
    prefix: string;
    options: { value: T; short: string; label: string; }[];
    value: T;
    onChange: (v: T) => void;
    sortDirection: SortIconDirection;
    onSort: () => void;
    active: boolean;
}) {
    const selected = options.find(o => o.value === value);
    return (
        <Flex align="center" justify="flex-end" gap="6px" w="100%" h="100%">
            <Menu>
                <MenuButton
                    as={Button}
                    rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3.5} />}
                    variant="ghost"
                    size="xs"
                    px={0}
                    minW={0}
                    height="auto"
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    color={active ? '#005BC4' : 'gray.600'}
                    _hover={{ bg: 'transparent', color: '#005BC4' }}
                    _active={{ bg: 'transparent' }}
                    whiteSpace="nowrap"
                    onClick={(e) => e.stopPropagation()}
                >
                    {prefix} {selected?.short}
                </MenuButton>
                <MenuList py={1} minW="160px" zIndex={2000}>
                    {options.map(opt => {
                        const isSelected = opt.value === value;
                        return (
                            <MenuItem
                                key={opt.value}
                                onClick={() => onChange(opt.value)}
                                fontSize="sm"
                                px={3}
                                py={2}
                                fontWeight={isSelected ? 'semibold' : 'normal'}
                                color={isSelected ? '#0071E3' : 'gray.700'}
                                _hover={{ bg: '#E6F1FD', color: '#0071E3' }}
                            >
                                <Flex align="center" justify="space-between" w="100%" gap={4}>
                                    <Text>{opt.label}</Text>
                                    {isSelected && <Icon as={TbCheck as React.ElementType} boxSize={3.5} />}
                                </Flex>
                            </MenuItem>
                        );
                    })}
                </MenuList>
            </Menu>
            <Box lineHeight={0} color={active ? '#005BC4' : 'gray.400'} flexShrink={0} cursor="pointer" onClick={onSort}>
                <SortArrowIcon direction={sortDirection} size={12} />
            </Box>
        </Flex>
    );
}

function round2(v: number) { return Math.round(v * 100) / 100; }

function addBreakdown(
    acc: ProductivityLocationBreakdown,
    value?: ProductivityLocationBreakdown
): ProductivityLocationBreakdown {
    if (!value) return acc;
    const totalParts = acc.totalParts + (value.totalParts || 0);
    const totalFlagged = (acc.totalFlaggedParts || 0) + (value.totalFlaggedParts || 0);
    return {
        orderCount: acc.orderCount + (value.orderCount || 0),
        orderVolume: round2(acc.orderVolume + (value.orderVolume || 0)),
        quoteCount: acc.quoteCount + (value.quoteCount || 0),
        totalMissedOpportunity: round2(acc.totalMissedOpportunity + (value.totalMissedOpportunity || 0)),
        totalParts,
        incorrectPartNumbers: (acc.incorrectPartNumbers || 0) + (value.incorrectPartNumbers || 0),
        manuallyFlaggedParts: (acc.manuallyFlaggedParts || 0) + (value.manuallyFlaggedParts || 0),
        totalFlaggedParts: totalFlagged,
        pctPartsFlagged: totalParts > 0 ? round2((totalFlagged / totalParts) * 100) : 0,
    };
}

function getBreakdownDisplayValue(
    breakdown: ProductivityLocationBreakdown | undefined,
    col: ColumnDef
): number | null {
    if (!breakdown) return null;

    if (col.key === 'quotesPerOrder') {
        return breakdown.orderCount > 0 ? round2(breakdown.quoteCount / breakdown.orderCount) : 0;
    }

    if (!col.breakdownKey) return null;
    return breakdown[col.breakdownKey] ?? 0;
}

const TABLE_KEY = 'reporting_productivity';

// View selections persisted to localStorage on explicit Save. Column
// visibility/width/density persist separately via their own hooks.
interface PersistedProductivityView {
    sortBy: SortField | null;
    sortOrder: 'asc' | 'desc' | null;
    showPrior: boolean;
    userFilter: UserFilter;
    showBreakdown: boolean;
    dailyMetric: DailyMetric;
    weeklyMetric: WeeklyMetric;
}

const ProductivityTab = ({
    startDate,
    endDate,
    locationId,
    preset,
    isActive = true,
}: ProductivityTabProps) => {
    const { excludeEvs } = useReportingFilters();
    const sessionContext = useContext(SessionContext);
    const isAdmin = !!sessionContext?.isAdmin;

    // Last-saved selections (read once on mount); persisted on explicit Save.
    const savedView = useMemo(
        () => loadViewPrefs<Partial<PersistedProductivityView>>(`${TABLE_KEY}_view`, {}),
        []
    );

    const [sortBy, setSortBy] = useState<SortField | null>(savedView.sortBy ?? null);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(savedView.sortOrder ?? null);
    const [showPrior, setShowPrior] = useState(savedView.showPrior ?? false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [userFilter, setUserFilter] = useState<UserFilter>(savedView.userFilter ?? 'everyone');
    const [showBreakdown, setShowBreakdown] = useState(savedView.showBreakdown ?? false);
    const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
    const [inactiveExpanded, setInactiveExpanded] = useState(false);
    const [dailyMetric, setDailyMetric] = useState<DailyMetric>(savedView.dailyMetric ?? 'average');
    const [weeklyMetric, setWeeklyMetric] = useState<WeeklyMetric>(savedView.weeklyMetric ?? 'total');

    // Screen-time columns are two fixed columns (Daily / Weekly) whose displayed
    // aggregation is chosen via a header dropdown. These map the synthetic column
    // keys to the concrete item field backing the current selection.
    const resolveFieldKey = useCallback((col: ColumnDef): string => {
        if (col.aggregation === 'daily') return DAILY_FIELD[dailyMetric];
        if (col.aggregation === 'weekly') return WEEKLY_FIELD[weeklyMetric];
        return col.key as string;
    }, [dailyMetric, weeklyMetric]);

    const resolveSortKey = useCallback((key: SortField): string => {
        if (key === 'dailyActiveToggle') return DAILY_FIELD[dailyMetric];
        if (key === 'weeklyActiveToggle') return WEEKLY_FIELD[weeklyMetric];
        return key as string;
    }, [dailyMetric, weeklyMetric]);

    const getColumnValue = useCallback(
        (item: Record<string, unknown>, col: ColumnDef): number =>
            (item[resolveFieldKey(col)] as number) ?? 0,
        [resolveFieldKey]
    );

    // Always-current snapshot of persistable selections + snapshot taken when the
    // settings drawer opens, so Save/Discard never close over stale state.
    const liveViewRef = useRef<PersistedProductivityView>();
    liveViewRef.current = { sortBy, sortOrder, showPrior, userFilter, showBreakdown, dailyMetric, weeklyMetric };
    const drawerSnapshotRef = useRef<PersistedProductivityView | null>(null);

    const handleSettingsOpen = useCallback(() => {
        drawerSnapshotRef.current = liveViewRef.current ?? null;
    }, []);
    const handleSettingsSave = useCallback(() => {
        if (liveViewRef.current) saveViewPrefs(`${TABLE_KEY}_view`, liveViewRef.current);
        drawerSnapshotRef.current = null;
    }, []);
    const handleSettingsDiscard = useCallback(() => {
        const snap = drawerSnapshotRef.current;
        if (snap) {
            setSortBy(snap.sortBy);
            setSortOrder(snap.sortOrder);
            setShowPrior(snap.showPrior);
            setUserFilter(snap.userFilter);
            setShowBreakdown(snap.showBreakdown);
            setDailyMetric(snap.dailyMetric);
            setWeeklyMetric(snap.weeklyMetric);
        }
        drawerSnapshotRef.current = null;
    }, []);

    const comparison: ReportingComparison | undefined = getReportingComparison(preset);
    const comparisonSupported = isReportingComparisonSupported(preset);
    const effectiveShowPrior = showPrior && comparisonSupported;

    useEffect(() => {
        if (!comparisonSupported) {
            setShowPrior(false);
        }
    }, [comparisonSupported]);

    const { data, isLoading } = useProductivityStats(startDate, endDate, {
        locationId,
        preset,
        comparison,
        excludeEvs,
        enabled: isActive,
    });

    const roleVisibleColumns = useMemo(() => {
        return ALL_COLUMNS.filter(col => {
            if (col.adminOnly && !isAdmin) return false;
            if (!col.restrictedToRoles) return true;
            return false;
        });
    }, [isAdmin]);

    const allColumnKeys = useMemo(() => roleVisibleColumns.map(c => c.key as string), [roleVisibleColumns]);

    const { settings: tableUiSettings, updateSettings: updateTableUi, tableSettingsSx } = useTableSettings(`${TABLE_KEY}_ui`);
    const { widths: colWidths, updateWidth, resetWidths, hasCustomWidths } = useColumnWidths(TABLE_KEY);
    const columnPrefs = useReportingColumnPrefs(
        `${TABLE_KEY}_cols`,
        allColumnKeys,
        ['name'],
        ['medianMissedOpportunity'],
    );

    const visibleColumns = useMemo(() => {
        const orderedKeys = columnPrefs.visibleOrderedKeys;
        if (orderedKeys.length === 0) return roleVisibleColumns;
        return orderedKeys
            .map(key => roleVisibleColumns.find(c => c.key === key))
            .filter(Boolean) as ColumnDef[];
    }, [roleVisibleColumns, columnPrefs.visibleOrderedKeys]);

    const columnLabels = useMemo(() => {
        const labels: Record<string, string> = { name: 'Name' };
        roleVisibleColumns.forEach((c) => { labels[c.key] = c.label; });
        return labels;
    }, [roleVisibleColumns]);

    const columnDrawerRows = useMemo(() => {
        return prefsToNestedRows(
            columnPrefs.prefs,
            allColumnKeys,
            columnLabels,
            PRODUCTIVITY_COLUMN_GROUPS,
            ['name'],
        );
    }, [columnPrefs.prefs, allColumnKeys, columnLabels]);

    const filteredItems = useMemo(() => {
        if (!data?.items) return [];
        let items = data.items;
        if (userFilter === 'central-parts') {
            items = items.filter(item => item.position === 'central-parts');
        }
        if (!searchQuery.trim()) return items;
        const q = searchQuery.trim().toLowerCase();
        return items.filter(item =>
            `${item.firstName} ${item.lastName}`.toLowerCase().includes(q)
        );
    }, [data?.items, searchQuery, userFilter]);

    const filteredInactive = useMemo((): ProductivityInactiveUser[] => {
        let users = data?.inactiveUsers ?? [];
        if (userFilter === 'central-parts') {
            users = users.filter(u => u.position === 'central-parts');
        }
        if (!searchQuery.trim()) return users;
        const q = searchQuery.trim().toLowerCase();
        return users.filter(u =>
            `${u.firstName} ${u.lastName}`.toLowerCase().includes(q)
        );
    }, [data?.inactiveUsers, searchQuery, userFilter]);

    const effectiveSortBy = sortBy ?? DEFAULT_SORT_BY;
    const effectiveSortOrder = sortOrder ?? DEFAULT_SORT_ORDER;

    const sortedItems = useMemo(() => {
        if (!filteredItems.length) return [];
        const items = [...filteredItems];
        if (effectiveSortBy === 'name') {
            items.sort((a, b) => {
                const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                return effectiveSortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
            });
        } else {
            const sortKey = resolveSortKey(effectiveSortBy);
            items.sort((a, b) => {
                const valA = (a as any)[sortKey] ?? 0;
                const valB = (b as any)[sortKey] ?? 0;
                return effectiveSortOrder === 'asc' ? valA - valB : valB - valA;
            });
        }
        return items;
    }, [effectiveSortBy, effectiveSortOrder, filteredItems, resolveSortKey]);

    const filteredTotals = useMemo(() => {
        if (!sortedItems.length) return null;
        if (userFilter === 'everyone' && data?.totals) return data.totals;
        let quoteCount = 0, orderVolume = 0, totalParts = 0;
        let incorrectPartNumbers = 0, partsWithoutPartNumber = 0;
        let manuallyFlaggedParts = 0, totalFlaggedParts = 0;
        let totalMissedOpportunity = 0;
        let oemEndorsementCount = 0, expeditedCount = 0, dealerReferralCount = 0;
        let quotesOnlyCount = 0, potentialTotalCount = 0;
        // Order Count is the DISTINCT set of orders quoted across the filtered
        // users (an order quoted by several people counts once), mirroring the
        // backend total. orderedOrders now carries each user's quoted orders.
        const distinctOrderIds = new Set<string>();
        for (const item of sortedItems) {
            quoteCount += item.quoteCount;
            for (const o of item.orderedOrders || []) distinctOrderIds.add(o.orderId);
            orderVolume += item.orderVolume;
            totalParts += item.totalParts;
            incorrectPartNumbers += item.incorrectPartNumbers;
            partsWithoutPartNumber += item.partsWithoutPartNumber || 0;
            manuallyFlaggedParts += item.manuallyFlaggedParts;
            totalFlaggedParts += item.totalFlaggedParts;
            totalMissedOpportunity += item.totalMissedOpportunity;
            oemEndorsementCount += item.oemEndorsementCount || 0;
            expeditedCount += item.expeditedCount || 0;
            dealerReferralCount += item.dealerReferralCount || 0;
            quotesOnlyCount += item.quotesOnlyCount || 0;
            potentialTotalCount += item.potentialTotalCount || 0;
        }
        const vol = round2(orderVolume);
        const mo = round2(totalMissedOpportunity);
        const orderCount = distinctOrderIds.size;
        return {
            quoteCount,
            quotesPerOrder: orderCount > 0 ? round2(quoteCount / orderCount) : 0,
            orderCount,
            orderVolume: vol,
            incorrectPartNumbers,
            partsWithoutPartNumber,
            manuallyFlaggedParts,
            totalFlaggedParts,
            manualOverrides: 0,
            pctPartsFlagged: totalParts > 0 ? round2((totalFlaggedParts / totalParts) * 100) : 0,
            totalMissedOpportunity: mo,
            avgMissedOpportunity: orderCount > 0 ? round2(mo / orderCount) : 0,
            medianMissedOpportunity: 0,
            totalParts,
            pctMissedOpportunity: vol > 0 ? round2((mo / vol) * 100) : 0,
            oemEndorsementCount,
            expeditedCount,
            dealerReferralCount,
            quotesOnlyCount,
            potentialTotalCount,
        };
    }, [sortedItems, userFilter, data?.totals]);

    const isCentralPartsView = userFilter === 'central-parts';
    const aggregatedBreakdownFallback = useMemo(() => {
        if (!isCentralPartsView || sortedItems.length === 0) return null;

        let hasMy = false;
        let hasElsewhere = false;
        const emptyBd = (): ProductivityLocationBreakdown => ({
            orderCount: 0, orderVolume: 0, quoteCount: 0,
            totalMissedOpportunity: 0, totalParts: 0,
            incorrectPartNumbers: 0, manuallyFlaggedParts: 0, totalFlaggedParts: 0, pctPartsFlagged: 0,
        });
        let my: ProductivityLocationBreakdown = emptyBd();
        let elsewhere: ProductivityLocationBreakdown = emptyBd();

        for (const item of sortedItems) {
            if (item.myLocationsBreakdown) {
                hasMy = true;
                my = addBreakdown(my, item.myLocationsBreakdown);
            }
            if (item.elsewhereBreakdown) {
                hasElsewhere = true;
                elsewhere = addBreakdown(elsewhere, item.elsewhereBreakdown);
            }
        }

        if (!hasMy && !hasElsewhere) return null;
        return {
            my: hasMy ? my : undefined,
            elsewhere: hasElsewhere ? elsewhere : undefined,
        };
    }, [isCentralPartsView, sortedItems]);

    const hasAnyBreakdown = isCentralPartsView && (
        sortedItems.some(item => item.myLocationsBreakdown || item.elsewhereBreakdown || (item.myLocationsPerLocationBreakdown?.length ?? 0) > 0) ||
        !!aggregatedBreakdownFallback
    );

    const toggleExpanded = useCallback((userId: string) => {
        setExpandedUsers(prev => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    }, []);

    // Table-header sorting auto-persists immediately (merged into the saved blob),
    // unlike the drawer selections which only persist on explicit "Save changes".
    const persistSort = useCallback((nextBy: SortField | null, nextOrder: 'asc' | 'desc' | null) => {
        const current = loadViewPrefs<Partial<PersistedProductivityView>>(`${TABLE_KEY}_view`, {});
        saveViewPrefs(`${TABLE_KEY}_view`, { ...current, sortBy: nextBy, sortOrder: nextOrder });
    }, []);

    const handleSort = (field: SortField) => {
        const isCurrentField = field === effectiveSortBy;

        let nextBy: SortField | null;
        let nextOrder: 'asc' | 'desc' | null;
        if (!isCurrentField) {
            nextBy = field;
            nextOrder = 'desc';
        } else if (effectiveSortOrder === 'desc') {
            nextBy = field;
            nextOrder = 'asc';
        } else {
            nextBy = null;
            nextOrder = null;
        }
        setSortBy(nextBy);
        setSortOrder(nextOrder);
        persistSort(nextBy, nextOrder);
    };

    const getSortDirection = (field: SortField): SortIconDirection => {
        if (effectiveSortBy !== field) return 'none';
        return effectiveSortOrder === 'asc' ? 'asc' : 'desc';
    };

    const handleExportCSV = useCallback(() => {
        if (!sortedItems.length) return;
        const rows = sortedItems.map(item => ({
            firstName: item.firstName,
            lastName: item.lastName,
            position: item.position ? positionMap[item.position] || item.position : '',
            ...visibleColumns.reduce((acc, col) => {
                acc[col.key as string] = getColumnValue(item as unknown as Record<string, unknown>, col);
                return acc;
            }, {} as Record<string, any>),
        }));
        // Reflect the selected aggregation in the screen-time column headers.
        const dailyShort = DAILY_OPTIONS.find(o => o.value === dailyMetric)?.short ?? '';
        const weeklyShort = WEEKLY_OPTIONS.find(o => o.value === weeklyMetric)?.short ?? '';
        const headers = {
            ...CSV_HEADERS,
            dailyActiveToggle: `Daily Active Session – ${dailyShort} (min)`,
            weeklyActiveToggle: `Weekly Active Session – ${weeklyShort} (min)`,
        };
        exportToCsv(rows, 'productivity_by_person', headers);
    }, [sortedItems, visibleColumns, getColumnValue, dailyMetric, weeklyMetric]);

    const headerStyles = (field: SortField) => ({
        fontSize: 'xs' as const,
        fontWeight: 'semibold' as const,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: effectiveSortBy === field ? '#005BC4' : 'gray.600',
        _hover: { color: '#005BC4' },
        cursor: 'pointer' as const,
        transition: 'color 0.2s',
        whiteSpace: 'nowrap' as const,
        lineHeight: 1.3,
    });

    const totalColSpan = 2 + visibleColumns.length;

    const BTN_SIZE = '32px';

    const settingsExtraTabs = useMemo(() => [{
        key: 'options',
        label: 'Options',
        content: (
            <VStack align="stretch" spacing={4}>
                {comparisonSupported && (
                    <Flex
                        align="center"
                        justify="space-between"
                        cursor="pointer"
                        onClick={() => setShowPrior(v => !v)}
                    >
                        <HStack spacing={3}>
                            <Icon as={TbEye as React.ElementType} boxSize={5} color="gray.500" />
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                    Show prior period
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    Display change indicators vs. previous period
                                </Text>
                            </Box>
                        </HStack>
                        <Switch
                            isChecked={showPrior}
                            pointerEvents="none"
                            colorScheme="blue"
                            size="md"
                        />
                    </Flex>
                )}
                <ExcludeEvsToggle />
                {hasAnyBreakdown && (
                    <>
                        <Divider my={6} />
                        <Flex
                            align="center"
                            justify="space-between"
                            cursor="pointer"
                            onClick={() => setShowBreakdown(v => !v)}
                        >
                            <HStack spacing={3}>
                                <Icon as={TbMapPin as React.ElementType} boxSize={5} color="gray.500" />
                                <Box>
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                        My locations breakdown
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                        Show per-user location breakdown rows
                                    </Text>
                                </Box>
                            </HStack>
                            <Switch
                                isChecked={showBreakdown}
                                pointerEvents="none"
                                colorScheme="blue"
                                size="md"
                            />
                        </Flex>
                    </>
                )}
            </VStack>
        ),
    }], [showPrior, showBreakdown, hasAnyBreakdown, comparisonSupported]);

    const toolbarRow = (
        <Flex
            flexShrink={0}
            w="100%"
            align="flex-end"
            justify="space-between"
            borderBottom="1.5px solid"
            borderColor="gray.200"
            mb={2}
            pb={0}
        >
            {/* Left: tabs (Everyone / Central Parts) */}
            <Flex gap={0} flexShrink={0}>
                {VIEW_MODE_CONFIG.map(({ key, label, icon }) => (
                    <Box
                        key={key}
                        as="button"
                        type="button"
                        onClick={() => setUserFilter(key)}
                        fontWeight="medium"
                        fontSize="sm"
                        color={userFilter === key ? 'gray.800' : 'gray.500'}
                        bg="transparent"
                        border="none"
                        borderBottom="1.5px solid"
                        borderColor={userFilter === key ? 'gray.500' : 'transparent'}
                        mb="-1.5px"
                        py={1.5}
                        px={3}
                        cursor="pointer"
                        transition="all 0.15s"
                        _hover={{
                            color: userFilter === key ? 'gray.800' : 'gray.700',
                        }}
                    >
                        <HStack spacing={1.5}>
                            <Icon as={icon} boxSize={3.5} />
                            <Text>{label}</Text>
                        </HStack>
                    </Box>
                ))}
                {!isLoading && data?.items && (
                    <Flex align="center" px={2} py={2} mb="-1.5px">
                        <Text fontSize="xs" color="gray.400" fontWeight="medium" whiteSpace="nowrap">
                            {filteredItems.length}{filteredItems.length !== data.items.length ? ` / ${data.items.length}` : ''} {filteredItems.length === 1 ? 'person' : 'people'}
                        </Text>
                    </Flex>
                )}
            </Flex>
            {/* Right: search, settings, fullscreen, download */}
            <HStack spacing={2} align="flex-end" pb={1} pt={1}>
                <InputGroup size="sm" w="200px">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={3.5} />
                    </InputLeftElement>
                    <Input
                        placeholder="Search by name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        borderColor="gray.300"
                        borderRadius="md"
                        h={BTN_SIZE}
                        fontSize="sm"
                        _placeholder={{ color: 'gray.400' }}
                        _hover={{ borderColor: 'gray.400' }}
                        _focus={{ borderColor: '#0071E3', boxShadow: '0 0 0 1px #0071E3' }}
                        bg="white"
                    />
                </InputGroup>
                <ReportingTableSettingsDrawer
                    settings={tableUiSettings}
                    onUpdateSettings={updateTableUi}
                    hasCustomColumnWidths={hasCustomWidths}
                    onResetColumnWidths={resetWidths}
                    columns={{
                        rows: columnDrawerRows,
                        onToggle: columnPrefs.toggleVisible,
                        onReorderDrag: columnPrefs.reorderByKey,
                        onResetColumns: columnPrefs.reset,
                        groups: PRODUCTIVITY_COLUMN_GROUPS,
                        getDefaultRows: () => prefsToNestedRows(
                            {
                                order: allColumnKeys,
                                hidden: ['medianMissedOpportunity'],
                            },
                            allColumnKeys,
                            columnLabels,
                            PRODUCTIVITY_COLUMN_GROUPS,
                            ['name'],
                        ),
                    }}
                    onCommitColumns={(rows) => {
                        columnPrefs.setPrefs(
                            nestedRowsToPrefs(rows, allColumnKeys, PRODUCTIVITY_COLUMN_GROUPS),
                        );
                    }}
                    extraTabs={settingsExtraTabs}
                    useAccordionLayout
                    onBeforeOpen={handleSettingsOpen}
                    onSaveChanges={handleSettingsSave}
                    onDiscardChanges={handleSettingsDiscard}
                />
                <Tooltip label={isFullScreen ? 'Exit full screen' : 'Full screen table'} placement="top" hasArrow fontSize="xs">
                    <IconButton
                        aria-label={isFullScreen ? 'Exit full screen' : 'Full screen table'}
                        icon={<Icon as={(isFullScreen ? TbMinimize : TbMaximize) as React.ElementType} boxSize={4} />}
                        size="sm"
                        h={BTN_SIZE}
                        w={BTN_SIZE}
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.600"
                        borderRadius="md"
                        _hover={{ bg: 'gray.50' }}
                        _active={{ bg: 'gray.100' }}
                        onClick={() => setIsFullScreen(v => !v)}
                    />
                </Tooltip>
                <Tooltip label="Export to CSV" hasArrow placement="top">
                    <IconButton
                        aria-label="Export to CSV"
                        icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />}
                        size="sm"
                        h={BTN_SIZE}
                        w={BTN_SIZE}
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.600"
                        borderRadius="md"
                        _hover={{ bg: 'gray.50' }}
                        _active={{ bg: 'gray.100' }}
                        onClick={handleExportCSV}
                        isDisabled={!sortedItems.length}
                    />
                </Tooltip>
            </HStack>
        </Flex>
    );

    return (
        <ReportingFullScreenShell
            isOpen={isFullScreen}
            onClose={() => setIsFullScreen(false)}
            fullscreenToolbar={isFullScreen ? toolbarRow : undefined}
        >
            <VStack
                spacing={0}
                align="stretch"
                w="100%"
                h="100%"
                pt={0}
                flex={1}
                minW={0}
                minH={0}
                flexGrow={isFullScreen ? 1 : undefined}
                overflow={isFullScreen ? 'hidden' : undefined}
            >
                {!isFullScreen && toolbarRow}

                <Box
                    w="100%"
                    flex={1}
                    minW={0}
                    minH={0}
                    overflowX="auto"
                    bg="white"
                    {...(!isFullScreen && {
                        border: '1px solid',
                        borderColor: 'gray.200',
                        borderRadius: 'lg',
                    })}
                    sx={{
                        ...tableSettingsSx,
                        overscrollBehavior: 'contain',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#cbd5e0 transparent',
                        '& table': {
                            width: 'max-content !important',
                            minWidth: '100%',
                        },
                        '& thead': {
                            zIndex: '3 !important',
                        },
                        '& th': {
                            ...(tableSettingsSx['& th'] || {}),
                            borderBottomWidth: '0 !important',
                            boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
                            bg: 'gray.50',
                        },
                        '& th:first-of-type': {
                            position: 'sticky',
                            left: 0,
                            zIndex: 5,
                            bg: 'gray.50',
                        },
                        '& td:first-of-type': {
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            bg: 'white',
                        },
                        '& tr[data-total-row] > td:first-of-type': {
                            bg: 'blue.50 !important',
                        },
                        '& tr:hover > td:first-of-type': {
                            bg: 'gray.50',
                        },
                        '& tr[data-total-row]:hover > td:first-of-type': {
                            bg: 'blue.50 !important',
                        },
                        '& tr[data-breakdown-row="my"] > td:first-of-type': {
                            bg: 'blue.50 !important',
                        },
                        '& tr[data-breakdown-row="my"]:hover > td:first-of-type': {
                            bg: 'blue.100 !important',
                        },
                        '& tr[data-breakdown-row="elsewhere"] > td:first-of-type': {
                            bg: 'orange.50 !important',
                        },
                        '& tr[data-breakdown-row="elsewhere"]:hover > td:first-of-type': {
                            bg: 'orange.100 !important',
                        },
                    }}
                >
                    <Table variant="simple" size="sm">
                        <Thead position="sticky" top={0} zIndex={1}>
                            <Tr>
                                {/* Name */}
                                <ResizableTh
                                    columnKey="__name__"
                                    storedWidth={colWidths.__name__}
                                    onResize={updateWidth}
                                    position="sticky"
                                    left={0}
                                    top={0}
                                    bg="gray.50"
                                    zIndex={4}
                                    minW="200px"
                                    py={3}
                                    px={3}
                                    borderBottom="none"
                                    onClick={() => handleSort('name')}
                                    {...headerStyles('name')}
                                >
                                    <Flex align="center" gap="6px">
                                        <Text as="span">Name</Text>
                                        <Box lineHeight={0} color={effectiveSortBy === 'name' ? '#005BC4' : 'gray.400'} flexShrink={0}>
                                            <SortArrowIcon direction={getSortDirection('name')} size={12} />
                                        </Box>
                                    </Flex>
                                </ResizableTh>
                                {/* Position */}
                                <ResizableTh
                                    columnKey="__position__"
                                    storedWidth={colWidths.__position__}
                                    onResize={updateWidth}
                                    position="sticky"
                                    top={0}
                                    bg="gray.50"
                                    zIndex={2}
                                    minW="100px"
                                    w="120px"
                                    py={3}
                                    px={2}
                                    borderBottom="none"
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    textTransform="uppercase"
                                    letterSpacing="0.05em"
                                    color="gray.600"
                                    whiteSpace="nowrap"
                                >
                                    Position
                                </ResizableTh>
                                {/* Data columns */}
                                {visibleColumns.map(col => (
                                    <ResizableTh
                                        key={col.key}
                                        columnKey={col.key as string}
                                        storedWidth={colWidths[col.key as string]}
                                        onResize={updateWidth}
                                        position="sticky"
                                        top={0}
                                        bg="gray.50"
                                        zIndex={2}
                                        py={3}
                                        px={2}
                                        borderBottom="none"
                                        isNumeric
                                        onClick={col.aggregation ? undefined : () => handleSort(col.key)}
                                        {...headerStyles(col.key)}
                                        cursor={col.aggregation ? 'default' : 'pointer'}
                                    >
                                        {col.aggregation === 'daily' ? (
                                            <AggregationHeaderMenu
                                                prefix="Daily"
                                                options={DAILY_OPTIONS}
                                                value={dailyMetric}
                                                onChange={setDailyMetric}
                                                sortDirection={getSortDirection(col.key)}
                                                onSort={() => handleSort(col.key)}
                                                active={effectiveSortBy === col.key}
                                            />
                                        ) : col.aggregation === 'weekly' ? (
                                            <AggregationHeaderMenu
                                                prefix="Weekly"
                                                options={WEEKLY_OPTIONS}
                                                value={weeklyMetric}
                                                onChange={setWeeklyMetric}
                                                sortDirection={getSortDirection(col.key)}
                                                onSort={() => handleSort(col.key)}
                                                active={effectiveSortBy === col.key}
                                            />
                                        ) : (
                                            <Flex align="center" justify="flex-end" gap="6px">
                                                <Text as="span">{col.label}</Text>
                                                <Box lineHeight={0} color={effectiveSortBy === col.key ? '#005BC4' : 'gray.400'} flexShrink={0}>
                                                    <SortArrowIcon direction={getSortDirection(col.key)} size={12} />
                                                </Box>
                                            </Flex>
                                        )}
                                    </ResizableTh>
                                ))}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {isLoading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <Tr key={i}>
                                        <Td position="sticky" left={0} bg="white" zIndex={1} px={3}>
                                            <Skeleton h="16px" w="120px" />
                                        </Td>
                                        <Td px={2}><Skeleton h="16px" w="60px" /></Td>
                                        {visibleColumns.map(col => (
                                            <Td key={col.key} isNumeric px={2}>
                                                <Skeleton h="16px" w="60px" ml="auto" />
                                            </Td>
                                        ))}
                                    </Tr>
                                ))
                            ) : (
                                <>
                                    {sortedItems.length === 0 ? (
                                        <Tr>
                                            <Td colSpan={totalColSpan} textAlign="center" py={8}>
                                                <Text color="gray.500">No productivity data for this period</Text>
                                            </Td>
                                        </Tr>
                                    ) : (
                                        <>
                                            {sortedItems.map((item) => {
                                        const myLocationsBreakdown = item.myLocationsBreakdown || aggregatedBreakdownFallback?.my;
                                        const elsewhereBreakdown = item.elsewhereBreakdown || aggregatedBreakdownFallback?.elsewhere;
                                        const hasPerLocation = (item.myLocationsPerLocationBreakdown?.length ?? 0) > 0;
                                        const canExpand = showBreakdown && hasAnyBreakdown && !!(myLocationsBreakdown || elsewhereBreakdown || hasPerLocation);
                                        const isExpanded = canExpand && expandedUsers.has(item.userId);
                                        return (
                                            <Fragment key={item.userId}>
                                                <Tr
                                                    _hover={{ bg: 'gray.50' }}
                                                    cursor={canExpand ? 'pointer' : undefined}
                                                    onClick={canExpand ? () => toggleExpanded(item.userId) : undefined}
                                                >
                                                    <Td
                                                        position="sticky"
                                                        left={0}
                                                        bg="white"
                                                        zIndex={1}
                                                        px={3}
                                                        whiteSpace="nowrap"
                                                        fontWeight="medium"
                                                        color="gray.800"
                                                        overflow="hidden"
                                                        textOverflow="ellipsis"
                                                        maxW="250px"
                                                    >
                                                        <HStack spacing={1}>
                                                            {canExpand && (
                                                                <Icon
                                                                    as={TbChevronDown as React.ElementType}
                                                                    boxSize={3.5}
                                                                    color="gray.400"
                                                                    transform={isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                                                                    transition="transform 0.2s"
                                                                    flexShrink={0}
                                                                />
                                                            )}
                                                            <Text as="span">{item.firstName} {item.lastName}</Text>
                                                        </HStack>
                                                    </Td>
                                                    <Td px={2} whiteSpace="nowrap">
                                                        <Text fontSize="xs" color={item.position === 'central-parts' ? 'blue.600' : 'gray.500'}>
                                                            {item.position ? positionMap[item.position] || item.position : '—'}
                                                        </Text>
                                                    </Td>
                                                    {visibleColumns.map(col => {
                                                        const value = getColumnValue(item as unknown as Record<string, unknown>, col);
                                                        const changeValue = item.change?.[resolveFieldKey(col)];
                                                        const ordersForHover = col.key === 'orderCount'
                                                            ? item.orderedOrders
                                                            : col.key === 'quotesOnlyCount'
                                                                ? item.quotesOnlyOrders
                                                                : col.key === 'potentialTotalCount'
                                                                    ? item.potentialTotalOrders
                                                                    : col.key === 'partsWithoutPartNumber'
                                                                        ? item.partsWithoutPartNumberOrders
                                                                        : undefined;
                                                        const cellInner = (
                                                            <Flex align="center" justify="flex-end">
                                                                <Text>{col.format(value)}</Text>
                                                                {effectiveShowPrior && <ChangeIndicator value={changeValue} isNegative={col.isNegative} />}
                                                            </Flex>
                                                        );
                                                        return (
                                                            <Td key={col.key} isNumeric whiteSpace="nowrap" px={2}>
                                                                {ordersForHover && ordersForHover.length > 0 ? (
                                                                    <Flex justify="flex-end">
                                                                        <OrderListPopover orders={ordersForHover} label={col.label}>
                                                                            {cellInner}
                                                                        </OrderListPopover>
                                                                    </Flex>
                                                                ) : cellInner}
                                                            </Td>
                                                        );
                                                    })}
                                                </Tr>
                                                {isExpanded && (
                                                    <>
                                                        <Tr
                                                            bg="blue.50"
                                                            _hover={{ bg: 'blue.100' }}
                                                            data-breakdown-row="my"
                                                            sx={{ '& td': { color: 'blue.800' } }}
                                                        >
                                                            <Td
                                                                position="sticky"
                                                                left={0}
                                                                bg="blue.50"
                                                                zIndex={1}
                                                                px={3}
                                                                pl={canExpand ? 8 : 6}
                                                                whiteSpace="nowrap"
                                                                color="blue.800"
                                                                fontSize="xs"
                                                                fontWeight="semibold"
                                                            >
                                                                My Locations
                                                            </Td>
                                                            <Td px={2} />
                                                            {visibleColumns.map(col => {
                                                                const value = getBreakdownDisplayValue(myLocationsBreakdown, col);
                                                                if (value === null) return <Td key={col.key} isNumeric px={2}><Text color="gray.300">—</Text></Td>;
                                                                return (
                                                                    <Td key={col.key} isNumeric whiteSpace="nowrap" px={2}>
                                                                        <Text color="blue.800">{col.format(value)}</Text>
                                                                    </Td>
                                                                );
                                                            })}
                                                        </Tr>
                                                        {(item.myLocationsPerLocationBreakdown || []).map((loc) => (
                                                            <Tr
                                                                key={`${item.userId}-${loc.locationId}`}
                                                                bg="blue.50"
                                                                _hover={{ bg: 'blue.100' }}
                                                                data-breakdown-row="my-location"
                                                                sx={{ '& td': { color: 'blue.800', bg: 'blue.50' }, '&:hover td': { bg: 'blue.100' } }}
                                                            >
                                                                <Td
                                                                    position="sticky"
                                                                    left={0}
                                                                    bg="blue.50"
                                                                    zIndex={1}
                                                                    px={3}
                                                                    pl={canExpand ? 12 : 10}
                                                                    whiteSpace="nowrap"
                                                                    color="blue.800"
                                                                    fontSize="xs"
                                                                    fontWeight="medium"
                                                                    sx={{
                                                                        bg: 'blue.50 !important',
                                                                        color: 'blue.800 !important',
                                                                    }}
                                                                    _hover={{
                                                                        bg: 'blue.100 !important',
                                                                    }}
                                                                >
                                                                    {loc.locationName}
                                                                </Td>
                                                                <Td px={2} />
                                                                {visibleColumns.map(col => {
                                                                    const value = getBreakdownDisplayValue(loc, col);
                                                                    if (value === null) return <Td key={col.key} isNumeric px={2}><Text color="gray.300">—</Text></Td>;
                                                                    return (
                                                                        <Td key={col.key} isNumeric whiteSpace="nowrap" px={2}>
                                                                            <Text color="blue.800">{col.format(value)}</Text>
                                                                        </Td>
                                                                    );
                                                                })}
                                                            </Tr>
                                                        ))}
                                                        <Tr
                                                            bg="orange.50"
                                                            _hover={{ bg: 'orange.100' }}
                                                            data-breakdown-row="elsewhere"
                                                            sx={{ '& td': { color: 'orange.800' } }}
                                                        >
                                                            <Td
                                                                position="sticky"
                                                                left={0}
                                                                bg="orange.50"
                                                                zIndex={1}
                                                                px={3}
                                                                pl={canExpand ? 8 : 6}
                                                                whiteSpace="nowrap"
                                                                color="orange.800"
                                                                fontSize="xs"
                                                                fontWeight="semibold"
                                                            >
                                                                Elsewhere
                                                            </Td>
                                                            <Td px={2} />
                                                            {visibleColumns.map(col => {
                                                                const value = getBreakdownDisplayValue(elsewhereBreakdown, col);
                                                                if (value === null) return <Td key={col.key} isNumeric px={2}><Text color="gray.300">—</Text></Td>;
                                                                return (
                                                                    <Td key={col.key} isNumeric whiteSpace="nowrap" px={2}>
                                                                        <Text color="orange.800">{col.format(value)}</Text>
                                                                    </Td>
                                                                );
                                                            })}
                                                        </Tr>
                                                    </>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                            {filteredTotals && sortedItems.length > 1 && (
                                                <Tr bg="blue.50" fontWeight="bold" data-total-row>
                                                    <Td
                                                        position="sticky"
                                                        left={0}
                                                        bg="blue.50"
                                                        zIndex={1}
                                                        fontWeight="bold"
                                                        color="gray.800"
                                                        px={3}
                                                    >
                                                        Total
                                                    </Td>
                                                    <Td px={2} />
                                                    {visibleColumns.map(col => {
                                                        // Screen time is a per-person metric; a column-level
                                                        // avg/median/total across users would be misleading.
                                                        if (col.aggregation) {
                                                            return (
                                                                <Td key={col.key} isNumeric whiteSpace="nowrap" px={2}>
                                                                    <Flex align="center" justify="flex-end">
                                                                        <Text color="gray.400">—</Text>
                                                                    </Flex>
                                                                </Td>
                                                            );
                                                        }
                                                        const value = (filteredTotals as any)[col.key] ?? 0;
                                                        return (
                                                            <Td key={col.key} isNumeric whiteSpace="nowrap" px={2}>
                                                                <Flex align="center" justify="flex-end">
                                                                    <Text>{col.format(value)}</Text>
                                                                </Flex>
                                                            </Td>
                                                        );
                                                    })}
                                                </Tr>
                                            )}
                                        </>
                                    )}
                                    {filteredInactive.length > 0 && (
                                        <>
                                            <Tr
                                                bg="gray.50"
                                                cursor="pointer"
                                                _hover={{ bg: 'gray.100' }}
                                                onClick={() => setInactiveExpanded(prev => !prev)}
                                                data-inactive-header-row
                                                sx={{ '&:hover td': { bg: 'gray.100' } }}
                                            >
                                                <Td
                                                    position="sticky"
                                                    left={0}
                                                    bg="gray.50"
                                                    zIndex={1}
                                                    px={3}
                                                    colSpan={totalColSpan}
                                                >
                                                    <HStack spacing={1}>
                                                        <Icon
                                                            as={TbChevronDown as React.ElementType}
                                                            boxSize={3.5}
                                                            color="gray.400"
                                                            transform={inactiveExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                                                            transition="transform 0.2s"
                                                            flexShrink={0}
                                                        />
                                                        <Text fontWeight="medium" color="gray.600">
                                                            Inactive ({filteredInactive.length})
                                                        </Text>
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                            {inactiveExpanded && filteredInactive.map((user) => (
                                                <Tr
                                                    key={`inactive-${user.userId}`}
                                                    _hover={{ bg: 'gray.50' }}
                                                    data-inactive-user-row
                                                >
                                                    <Td
                                                        position="sticky"
                                                        left={0}
                                                        bg="white"
                                                        zIndex={1}
                                                        px={3}
                                                        whiteSpace="nowrap"
                                                        fontWeight="medium"
                                                        color="gray.800"
                                                        overflow="hidden"
                                                        textOverflow="ellipsis"
                                                        maxW="250px"
                                                    >
                                                        <Text as="span">{user.firstName} {user.lastName}</Text>
                                                    </Td>
                                                    <Td px={2} whiteSpace="nowrap">
                                                        <Text fontSize="xs" color={user.position === 'central-parts' ? 'blue.600' : 'gray.500'}>
                                                            {user.position ? positionMap[user.position] || user.position : '—'}
                                                        </Text>
                                                    </Td>
                                                    {visibleColumns.map(col => (
                                                        <Td key={col.key} isNumeric whiteSpace="nowrap" px={2}>
                                                            <Text color="gray.300">—</Text>
                                                        </Td>
                                                    ))}
                                                </Tr>
                                            ))}
                                        </>
                                    )}
                                </>
                            )}
                        </Tbody>
                    </Table>
                </Box>
            </VStack>
        </ReportingFullScreenShell>
    );
};

export default ProductivityTab;
